/* eslint-disable react-refresh/only-export-components */
// 同文件导出组件(DhComposePanel)+ useDhComposeEditor hook 为有意模式(与视频线
// VideoComposePanel 同构);react-refresh 仅关注 HMR,不影响运行时。
/**
 * 数字人「成片编辑」面板(2026-09-09 老板:字幕能不能移动 / logo 能不能加,
 * 与 AI 视频结果页保持一致)。
 *
 * 与视频线 VideoComposePanel 同构裁剪版:同一块 DragOverlayCanvas(logo 拖拽 +
 * 字幕带上下拖/宽度手柄所见即所得)、同一份 shared/lib/video/subtitleStyle 档位语义、
 * 同一个后端 DTO(VideoComposeOptions)。裁掉的:声音/BGM/QR 开关(数字人线 BGM 在
 * 创建时定、QR 视频线也没放开)、多镜相关一切。
 *
 * ★composeBase 闸(有无烧字幕前底片):
 *   有 → 字幕带可拖可改样式可开关(ai 从底片重烧);
 *   无(0909 前的老片)→ 字幕控件整块不渲染并说明原因 —— 成片里字幕已烧死,
 *   重烧=双层字幕;logo 照样能叠(叠加不碰既有画面内容)。
 * ★零计费:重合成不扣积分,按钮上明说 —— 免得用户以为又要花一份钱不敢点。
 *
 * ★2026-09-11 对齐视频线 D 版三列形制(老板反馈:「成片编辑」整块太大、挂在视频下方,
 *   要求与 AI 视频详情页一致):原先整块竖排全宽挂在详情页骨架**下方**,现在拆成
 *   `useDhComposeEditor` 返回的 `{ canvas, controls }` 两半 —— canvas 进中栏「调版式」
 *   tab,controls 进左侧 212px 工具栏。做成 hook 而不是 render-prop的理由与视频线
 *   `useComposeEditor` 头注同一条:两栏之间还夹着播放器 / 下载 / Dock 这些与「成片编辑」
 *   毫无关系的东西,不该挂在它的子树里(挂上去 = 编辑面板一旦不渲染,整页正文跟着消失)。
 *   逻辑一字不动,只是把原先竖排一整块拆成两半分居两栏。
 * ★hook 必须能在 task 未加载时安全调用:详情页要在「加载中」早返回**之前**调它(理由同
 *   视频线 useComposeEditor 头注 —— 放在早返回之后会让首帧/次帧 hook 数量不一致,React
 *   直接抛「Rendered more hooks than during the previous render」整页白屏)。这意味着
 *   `hadSubtitles` 这类「按 task 算出来」的 prop,第一次调用时给的很可能是 task 还没到手时
 *   的默认值(`st` 为 null → `hadSubtitles=true`)——`subtitleOn` 不能直接拿它当
 *   `useState` 初值(那样会把「加载中」那一刻的默认值锁死,真数据到手后也不会再同步)。
 *   这里用 `baseImageUrl` 非空(与 composeEditable 同源信号:两者都要求 finalAsset.thumbUrl
 *   已就绪)当「真任务已到手」的门槛,配合 `subtitleOn == null` 只补一次值 —— 用户手动切过
 *   开关之后 `subtitleOn` 不再是 null,这道门槛也就不会再覆盖用户的选择(与视频线
 *   `useComposeEditor` 里 `overlay == null` 才补值是同一条防重复初始化的写法)。
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Captions, Loader2, Sparkles } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Switch } from "@/shared/ui/switch";
import { useBrandAssets } from "@/entities/brand-asset";
import { DragOverlayCanvas } from "@/shared/ui/overlay/DragOverlayCanvas";
import { DEFAULT_OVERLAY, type OverlayConfig } from "@/shared/lib/overlay/overlayConfig";
import {
  DEFAULT_SUBTITLE_STYLE,
  applySubtitleDrag,
  applySubtitlePosition,
  applySubtitleWidth,
  previewAnchorY,
  previewFontPx,
  previewOutlinePx,
  subtitleComposeOptions,
  type SubtitleStyle,
} from "@/shared/lib/video/subtitleStyle";
import { cn } from "@/shared/lib/utils";
import type { DhComposeOptions } from "../../api/digitalHumanService";

/** 预览字号/描边换算的画布参考高(与视频面板同一量级;真实字号由引擎按成片高算)。 */
const CANVAS_PREVIEW_H = 540;

export interface DhComposePanelProps {
  /** 有无重合成底片(任务 DTO composeBase)。 */
  composeBase: boolean;
  /** 原单是否烧了字幕(settings.burnSubtitles===false 为关;缺省=烧)。 */
  hadSubtitles: boolean;
  /** 字幕带预览文案(口播第一句;没有就不画带)。 */
  subtitleSample: string | null;
  /** 画布底图(成片封面帧)。task 未加载时为空串——那一刻 canvas/controls 还不会被渲染进树。 */
  baseImageUrl: string;
  applying: boolean;
  onApply: (options: DhComposeOptions) => void | Promise<void>;
}

/**
 * 成片编辑的状态与两半 UI(画布 / 控件)。用法见文件头注。
 */
export function useDhComposeEditor({
  composeBase,
  hadSubtitles,
  subtitleSample,
  baseImageUrl,
  applying,
  onApply,
}: DhComposePanelProps) {
  const { data: brand } = useBrandAssets();
  const logos = brand?.logos ?? [];

  const [overlay, setOverlay] = useState<OverlayConfig | null>(null);
  const [logoKey, setLogoKey] = useState<string | null>(null);
  // ★null = 还没拿到真任务时的「未初始化」态,不是「关」;见文件头注。
  const [subtitleOn, setSubtitleOn] = useState<boolean | null>(null);
  useEffect(() => {
    if (subtitleOn == null && baseImageUrl) setSubtitleOn(hadSubtitles);
  }, [subtitleOn, hadSubtitles, baseImageUrl]);
  const subtitleOnValue = subtitleOn ?? hadSubtitles;
  const [subtitle, setSubtitle] = useState<SubtitleStyle>(DEFAULT_SUBTITLE_STYLE);

  const activeLogo = logos.find((l) => l.objectKey === logoKey) ?? null;
  const logoUrl = activeLogo?.signedUrl ?? undefined;

  const enableLogo = (on: boolean) => {
    if (!on) {
      setOverlay(null);
      setLogoKey(null);
      return;
    }
    const first = logos.find((l) => l.isDefault) ?? logos[0];
    if (!first) return;
    setLogoKey(first.objectKey);
    // QR 不放(视频线也在 flag 后面);logo 默认落左上角 DEFAULT_OVERLAY 位。
    setOverlay({ ...DEFAULT_OVERLAY, qr: null, logoUploadId: first.objectKey });
  };

  const subtitleEditable = composeBase && !!subtitleSample;

  const handleApply = () => {
    const options: DhComposeOptions = {
      overlayConfig: overlay,
      // 无底片时不发字幕相关键:ai 侧 subtitlesAlreadyBurned 本来就跳过,不发=不误导回显
      ...(composeBase
        ? {
            subtitleEnabled: subtitleOnValue,
            ...(subtitleOnValue ? subtitleComposeOptions(subtitle) : {}),
          }
        : {}),
    };
    void onApply(options);
  };

  const posBtn = (pos: SubtitleStyle["position"], label: string) => (
    <button
      type="button"
      key={pos}
      disabled={applying}
      onClick={() => setSubtitle((s0) => applySubtitlePosition(s0, pos))}
      className={cn(
        "rounded border px-2 py-1 text-xs transition",
        subtitle.position === pos && subtitle.anchorY == null
          ? "border-primary bg-primary/10 font-medium text-primary"
          : "text-muted-foreground hover:bg-muted"
      )}
    >
      {label}
    </button>
  );

  const optBtn = <T extends string>(cur: T, val: T, label: string, set: (v: T) => void) => (
    <button
      type="button"
      key={val}
      disabled={applying}
      onClick={() => set(val)}
      className={cn(
        "rounded border px-2 py-1 text-xs transition",
        cur === val
          ? "border-primary bg-primary/10 font-medium text-primary"
          : "text-muted-foreground hover:bg-muted"
      )}
    >
      {label}
    </button>
  );

  // canvas:画布 + logo 拖拽提示(与视频线同一块 DragOverlayCanvas)。
  const canvasNode = (
    <div className="space-y-1.5">
      <DragOverlayCanvas
        baseImageUrl={baseImageUrl}
        brandAssets={{ logoUrl }}
        value={overlay}
        onChange={setOverlay}
        readOnly={applying}
        subtitle={
          subtitleEditable && subtitleOnValue && subtitleSample
            ? {
                anchorY: previewAnchorY(subtitle),
                widthPct: subtitle.widthPct ?? 0.86,
                sampleText: subtitleSample,
                fontPx: previewFontPx(subtitle, CANVAS_PREVIEW_H),
                outlinePx: previewOutlinePx(subtitle, CANVAS_PREVIEW_H),
              }
            : null
        }
        onSubtitleChange={
          applying
            ? undefined
            : ({ anchorY, widthPct }) =>
                setSubtitle((s0) => applySubtitleWidth(applySubtitleDrag(s0, anchorY), widthPct))
        }
      />
      {overlay?.logo && (
        <p className="text-2xs leading-snug text-muted-foreground">
          在上面画布里拖 logo 到想要的位置
        </p>
      )}
    </div>
  );

  // controls:开关 + 选择 + 应用,竖排适配 212px 窄工具栏。
  const controlsNode = (
    <div className="flex flex-col gap-3">
      {/* logo 行 */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Switch
            id="dh-compose-logo"
            checked={!!overlay?.logo}
            onCheckedChange={enableLogo}
            disabled={applying || logos.length === 0}
            aria-label="叠品牌 logo"
          />
          <label htmlFor="dh-compose-logo" className="text-sm">
            品牌 logo
          </label>
        </div>
        {logos.length === 0 && (
          <Link
            to="/brand-assets"
            className="text-xs text-primary underline-offset-2 hover:underline"
          >
            还没上传过 logo,去配置品牌资产 ▸
          </Link>
        )}
        {overlay?.logo && logos.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {logos.map((l) => (
              <button
                type="button"
                key={l.id}
                disabled={applying}
                onClick={() => {
                  setLogoKey(l.objectKey);
                  setOverlay((o) => (o ? { ...o, logoUploadId: l.objectKey } : o));
                }}
                className={cn(
                  "rounded border px-2 py-1 text-xs transition",
                  logoKey === l.objectKey
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {l.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 字幕行:只有有底片的片才给(老片字幕烧死在画面里,重烧=双层,诚实说明) */}
      {subtitleEditable ? (
        <div className="space-y-2 rounded-lg border p-2.5">
          <div className="flex items-center gap-2">
            <Captions className="h-4 w-4 text-muted-foreground" />
            <Switch
              id="dh-compose-subtitle"
              checked={subtitleOnValue}
              onCheckedChange={setSubtitleOn}
              disabled={applying}
              aria-label="烧录字幕"
            />
            <label htmlFor="dh-compose-subtitle" className="text-sm">
              字幕
            </label>
          </div>
          {subtitleOnValue && (
            <>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-2xs text-muted-foreground">位置</span>
                  {subtitle.anchorY != null && (
                    <span className="text-2xs text-muted-foreground">(已拖到自定义位置)</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {posBtn("top", "上")}
                  {posBtn("center", "中")}
                  {posBtn("bottom", "下")}
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-2xs text-muted-foreground">大小</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {optBtn(subtitle.fontScale, "small", "小", (v) =>
                    setSubtitle((s0) => ({ ...s0, fontScale: v }))
                  )}
                  {optBtn(subtitle.fontScale, "medium", "中", (v) =>
                    setSubtitle((s0) => ({ ...s0, fontScale: v }))
                  )}
                  {optBtn(subtitle.fontScale, "large", "大", (v) =>
                    setSubtitle((s0) => ({ ...s0, fontScale: v }))
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-2xs text-muted-foreground">描边</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {optBtn(subtitle.outline, "none", "无", (v) =>
                    setSubtitle((s0) => ({ ...s0, outline: v }))
                  )}
                  {optBtn(subtitle.outline, "thin", "细", (v) =>
                    setSubtitle((s0) => ({ ...s0, outline: v }))
                  )}
                  {optBtn(subtitle.outline, "thick", "粗", (v) =>
                    setSubtitle((s0) => ({ ...s0, outline: v }))
                  )}
                </div>
              </div>
            </>
          )}
          <p className="text-2xs text-muted-foreground">
            字幕带可以在画布里上下拖、两侧手柄改宽;应用后按新位置重新烧录。
          </p>
        </div>
      ) : (
        <p className="text-2xs text-muted-foreground">
          {composeBase
            ? "本片没有可编辑的字幕。"
            : "这条片生成得比较早,字幕已固定在画面里改不了位置;logo 可以照常添加。之后新出的片都支持拖字幕。"}
        </p>
      )}

      {/* 应用按钮:竖排,w-full 适配窄栏 */}
      <div className="mt-auto flex flex-col gap-1.5">
        <Button
          size="sm"
          onClick={handleApply}
          disabled={applying}
          data-testid="dh-compose-apply"
          className="w-full cursor-pointer"
        >
          {applying ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              合成中…
            </>
          ) : (
            "应用到成片(不扣积分)"
          )}
        </Button>
        <span className="text-2xs text-muted-foreground">约 1~2 分钟,完成后本页自动更新</span>
      </div>
    </div>
  );

  return { canvas: canvasNode, controls: controlsNode };
}

/**
 * 薄兼容导出:两半拼回一张卡。详情页已改走 `useDhComposeEditor` 分栏挂载,不再用它;
 * 留给未来可能的独立弹层 / 旧调用方。
 */
export function DhComposePanel(props: DhComposePanelProps) {
  const { canvas, controls } = useDhComposeEditor(props);
  return (
    <Card data-testid="dh-compose-panel">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">成片编辑</span>
          <span className="text-2xs text-muted-foreground">
            字幕位置 / 品牌 logo,改完重合成 —— 不重新出片、不扣积分
          </span>
        </div>
        {canvas}
        {controls}
      </CardContent>
    </Card>
  );
}
